import { useEffect, useState } from "react";
import { Box, VStack } from "@chakra-ui/react";
import { Field, FormInput, FormSelect, FormSwitch } from "@/components/jobOffer/ui";
import { SectionCard, SectionHeader, Grid2 } from "@/components/jobOffer/ui/layout";
import { AddButton, ItemHeader } from "@/components/jobOffer/ui/listHelpers";
import { CampaignService } from "@/services/CampaignService";
import { extractBusinessErrorMessage } from "@/services/apiErrorMessage";
import GoogleSheetsConnectPanel from "@/components/campaign/GoogleSheetsConnectPanel";
import MicrosoftExcelConnectPanel from "@/components/campaign/MicrosoftExcelConnectPanel";

const TARGET_SYSTEM_OPTIONS = [
    { value: "GoogleSheets", label: "Google Sheets" },
    { value: "Excel", label: "Microsoft Excel / 365" },
    { value: "TwentyCrm", label: "Twenty CRM" },
    { value: "HubSpot", label: "HubSpot" },
    { value: "Pipedrive", label: "Pipedrive" },
    { value: "Salesforce", label: "Salesforce" },
    { value: "Airtable", label: "Airtable" },
    { value: "Erp", label: "ERP" },
    { value: "CrmClient", label: "CRM Klienta" },
    { value: "Api", label: "API" },
    { value: "Webhook", label: "Webhook" },
];

const TRIGGER_OPTIONS = [
    { value: "AfterEachResponse", label: "Po każdej odpowiedzi" },
    { value: "AfterStatusChange", label: "Po zmianie statusu" },
    { value: "AfterQualification", label: "Po kwalifikacji" },
    { value: "AfterConversationEnd", label: "Po zakończeniu rozmowy" },
    { value: "AfterMeetingBooked", label: "Po umówieniu spotkania" },
    { value: "AfterConversion", label: "Po konwersji" },
];

const ACTION_OPTIONS = [
    { value: "UpdateExistingRecord", label: "Aktualizuj istniejący rekord" },
    { value: "CreateNewRecord", label: "Utwórz nowy rekord" },
];

// Mirrors ResolveLeadFieldValue's switch in OutputIntegrationSyncBackgroundService.cs -- keep in sync
// manually, there's no shared enum backing these "source field" string keys. Anything not in this list
// falls through to Lead.AdditionalData there, which is why custom fields (below) use the raw field name.
const FIXED_SOURCE_FIELDS = [
    { value: "LeadId", label: "Lead ID" },
    { value: "Firstname", label: "Imię" },
    { value: "Lastname", label: "Nazwisko" },
    { value: "Company", label: "Firma" },
    { value: "JobTitle", label: "Stanowisko" },
    { value: "PhoneNumber", label: "Telefon" },
    { value: "Email", label: "E-mail" },
    { value: "Status", label: "Status" },
    { value: "Source", label: "Źródło" },
    { value: "CreatedAt", label: "Data utworzenia" },
    { value: "UpdatedAt", label: "Data aktualizacji" },
];

const newIntegration = () => ({
    id: crypto.randomUUID(), targetSystem: "GoogleSheets", trigger: "AfterStatusChange",
    action: "UpdateExistingRecord", isActive: true, updateSourceSheet: false, isConnected: false,
    spreadsheetUrl: "", sheetName: "", excelFileUrl: "", excelWorksheetName: "", excelConnectionId: null,
    keyTargetColumn: "Lead ID", fieldMapping: [],
});

const StepOutput = ({ values, onChange }) => {
    const set = (key, val) => onChange({ ...values, [key]: val });

    const [serviceAccountEmail, setServiceAccountEmail] = useState(null);
    // Task-29: connected Microsoft account email per integration, keyed by integration id -- only known
    // for this browser session (postMessage payload), not persisted (mirrors StepLeadSource.jsx).
    const [excelConnectedAccountEmails, setExcelConnectedAccountEmails] = useState({});

    useEffect(() => {
        CampaignService.getLeadSourceServiceAccountEmail()
            .then((data) => setServiceAccountEmail(data?.email ?? null))
            .catch(() => {});
    }, []);

    const checkAccess = async (spreadsheetUrl, sheetName) => {
        try {
            const data = await CampaignService.checkLeadSourceAccess(spreadsheetUrl, sheetName);
            return { isConnected: !!data?.isConnected, columns: data?.columns ?? [], error: data?.error ?? null };
        } catch (error) {
            return { isConnected: false, columns: [], error: extractBusinessErrorMessage(error) ?? "Nie udało się zweryfikować dostępu." };
        }
    };

    // Task-29: Excel Online equivalent of checkAccess.
    const checkExcelAccess = async (fileUrl, worksheetName, connectionId) => {
        try {
            const data = await CampaignService.checkExcelLeadSourceAccess(fileUrl, worksheetName, connectionId);
            return { isConnected: !!data?.isConnected, columns: data?.columns ?? [], error: data?.error ?? null };
        } catch (error) {
            return { isConnected: false, columns: [], error: extractBusinessErrorMessage(error) ?? "Nie udało się zweryfikować dostępu." };
        }
    };

    const integrations = values.outputIntegrations ?? [];
    const setIntegrations = (next) => set("outputIntegrations", next);
    const updateIntegration = (i, patch) => setIntegrations(integrations.map((o, idx) => idx === i ? { ...o, ...patch } : o));
    const removeIntegration = (i) => setIntegrations(integrations.filter((_, idx) => idx !== i));
    const addIntegration = () => setIntegrations([...integrations, newIntegration()]);

    // Every available field (fixed Lead columns + this campaign's custom fields, i.e. Lead.AdditionalData
    // keys) is always shown -- there's nothing to add/remove, only a "Pole docelowe" to optionally fill in.
    // Leaving it empty means the field isn't exported (enforced in campaignMapper.mapOutputIntegration).
    const availableFields = [
        ...FIXED_SOURCE_FIELDS,
        ...(values.customFieldMappings ?? [])
            .filter(c => c.fieldName)
            .map(c => ({ value: c.fieldName, label: c.fieldName })),
    ];

    const targetFieldFor = (integration, sourceField) =>
        (integration.fieldMapping ?? []).find(m => m.sourceField === sourceField)?.targetField ?? "";
    const setTargetFieldFor = (integrationIndex, sourceField, targetField) => {
        const integration = integrations[integrationIndex];
        const rest = (integration.fieldMapping ?? []).filter(m => m.sourceField !== sourceField);
        updateIntegration(integrationIndex, {
            fieldMapping: targetField ? [...rest, { sourceField, targetField }] : rest,
        });
    };

    return (
        <VStack w="full" align="stretch" gap={4}>
            <SectionCard>
                <SectionHeader number={21} title="Systemy docelowe" subtitle="Gdzie mają trafiać wyniki rozmów -- można skonfigurować wiele wyjść naraz" />
                <VStack align="stretch" gap={4}>
                    {integrations.map((o, i) => {
                        const isGoogleSheets = o.targetSystem === "GoogleSheets";
                        const isExcel = o.targetSystem === "Excel";
                        return (
                            <Box key={o.id ?? i} borderWidth={1} borderColor="border.primary" borderRadius={8} p={4}>
                                <ItemHeader
                                    title={TARGET_SYSTEM_OPTIONS.find(t => t.value === o.targetSystem)?.label || o.targetSystem}
                                    index={i}
                                    onRemove={() => removeIntegration(i)}
                                />
                                <VStack align="stretch" gap={3}>
                                    <FormSwitch label="Aktywne" checked={!!o.isActive} onChange={(v) => updateIntegration(i, { isActive: v })} />
                                    <Grid2>
                                        <Field label="System docelowy">
                                            <FormSelect value={o.targetSystem} onChange={(v) => updateIntegration(i, { targetSystem: v })} options={TARGET_SYSTEM_OPTIONS} placeholder="Wybierz system" />
                                        </Field>
                                        <Field label="Wyzwalacz">
                                            <FormSelect value={o.trigger} onChange={(v) => updateIntegration(i, { trigger: v })} options={TRIGGER_OPTIONS} placeholder="Wybierz wyzwalacz" />
                                        </Field>
                                    </Grid2>
                                    <Field label="Akcja">
                                        <FormSelect value={o.action} onChange={(v) => updateIntegration(i, { action: v })} options={ACTION_OPTIONS} placeholder="Wybierz akcję" />
                                    </Field>

                                    {isGoogleSheets && (
                                        <>
                                            <Grid2>
                                                <Field label="URL arkusza wynikowego">
                                                    <FormInput
                                                        value={o.spreadsheetUrl ?? ""}
                                                        onChange={(e) => updateIntegration(i, { spreadsheetUrl: e.target.value, isConnected: false })}
                                                        placeholder="https://docs.google.com/spreadsheets/d/..."
                                                    />
                                                </Field>
                                                <Field label="Nazwa zakładki">
                                                    <FormInput
                                                        value={o.sheetName ?? ""}
                                                        onChange={(e) => updateIntegration(i, { sheetName: e.target.value, isConnected: false })}
                                                        placeholder="np. Wyniki"
                                                    />
                                                </Field>
                                            </Grid2>
                                            <GoogleSheetsConnectPanel
                                                serviceAccountEmail={serviceAccountEmail}
                                                spreadsheetUrl={o.spreadsheetUrl}
                                                sheetName={o.sheetName}
                                                isConnected={!!o.isConnected}
                                                onCheckAccess={checkAccess}
                                                onConnected={() => updateIntegration(i, { isConnected: true })}
                                            />
                                            <Field label="Kolumna identyfikująca lead" hint='Nagłówek kolumny z Lead ID, po której dopasowywany jest wiersz (domyślnie "Lead ID")'>
                                                <FormInput value={o.keyTargetColumn ?? ""} onChange={(e) => updateIntegration(i, { keyTargetColumn: e.target.value })} placeholder="Lead ID" />
                                            </Field>
                                        </>
                                    )}

                                    {isExcel && (
                                        <>
                                            <Grid2>
                                                <Field label="Link do pliku Excel">
                                                    <FormInput
                                                        value={o.excelFileUrl ?? ""}
                                                        onChange={(e) => updateIntegration(i, { excelFileUrl: e.target.value, isConnected: false })}
                                                        placeholder="https://onedrive.live.com/... lub https://firma.sharepoint.com/..."
                                                    />
                                                </Field>
                                                <Field label="Nazwa arkusza (worksheet)">
                                                    <FormInput
                                                        value={o.excelWorksheetName ?? ""}
                                                        onChange={(e) => updateIntegration(i, { excelWorksheetName: e.target.value, isConnected: false })}
                                                        placeholder="np. Wyniki"
                                                    />
                                                </Field>
                                            </Grid2>
                                            <MicrosoftExcelConnectPanel
                                                campaignId={values.id}
                                                purpose={`Output:${o.id}`}
                                                fileUrl={o.excelFileUrl}
                                                worksheetName={o.excelWorksheetName}
                                                connectionId={o.excelConnectionId}
                                                connectedAccountEmail={excelConnectedAccountEmails[o.id]}
                                                isConnected={!!o.isConnected}
                                                onCheckAccess={checkExcelAccess}
                                                onAccountConnected={({ connectionId, connectedAccountEmail }) => {
                                                    updateIntegration(i, { excelConnectionId: connectionId, isConnected: false });
                                                    setExcelConnectedAccountEmails((prev) => ({ ...prev, [o.id]: connectedAccountEmail ?? null }));
                                                }}
                                                onConnected={() => updateIntegration(i, { isConnected: true })}
                                            />
                                            <Field label="Kolumna identyfikująca lead" hint='Nagłówek kolumny z Lead ID, po której dopasowywany jest wiersz (domyślnie "Lead ID")'>
                                                <FormInput value={o.keyTargetColumn ?? ""} onChange={(e) => updateIntegration(i, { keyTargetColumn: e.target.value })} placeholder="Lead ID" />
                                            </Field>
                                        </>
                                    )}

                                    <Box borderTopWidth={1} borderColor="border.primary" pt={3} mt={1}>
                                        <Box fontSize="xs" fontWeight={600} color="text.grey" textTransform="uppercase" letterSpacing="wider" mb={2}>
                                            Mapowanie pól wyjściowych
                                        </Box>
                                        <VStack align="stretch" gap={2}>
                                            {availableFields.map((f) => (
                                                <Box key={f.value} display="flex" alignItems="center" gap={4}>
                                                    <Box fontSize="sm" color="text.white" flex={1} minW={0} title={f.label} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                                                        {f.label}
                                                    </Box>
                                                    <Box flex={1}>
                                                        <FormInput
                                                            value={targetFieldFor(o, f.value)}
                                                            onChange={(e) => setTargetFieldFor(i, f.value, e.target.value)}
                                                            placeholder='np. "AI Status" -- puste = pole nie jest eksportowane'
                                                        />
                                                    </Box>
                                                </Box>
                                            ))}
                                        </VStack>
                                    </Box>
                                </VStack>
                            </Box>
                        );
                    })}
                    <AddButton label="Dodaj wyjście" onClick={addIntegration} />
                </VStack>
            </SectionCard>
        </VStack>
    );
};

export default StepOutput;
